import debounce from 'debounce-promise'
import moment from 'moment'
import { useContext, useEffect, useMemo, useRef } from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

import { useDatesOrGuestsChanged } from './useDatesOrGuestsChanged'
import {
  useUpdateSavedItemBookmarkHandlers,
} from './useUpdateSavedItemSnackbarHandlers'

import { useOfferPageRate } from 'components/OfferPage/hooks/useOfferPageRate'
import { getDatesAndGuestsFromOfferPageState } from 'components/OfferPage/utils'
import { DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { useUpdateTripItem } from '../api/tripItem'
import { useTrip } from '../api/trip'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import type {
  AccommodationItem,
  BookmarkDatesAndOccupancy,
  LETripItem,
  TripItem,
} from 'tripPlanner/types/tripItem'
import { createHomeAndVillasItemPayload } from 'tripPlanner/utils/itemConversions/bookmarks/homeAndVillas'

export const getHomeAndVillasUpdateSuccessSnackbarMessage = <
  T extends {
    checkIn?: string
    checkOut?: string
    occupancies?: Array<App.Occupants>
  },
>(intl: IntlShape, {
  checkIn,
  checkOut,
  occupancies,
}: T): string => {
  const dates =
    checkIn && checkOut ?
      `${moment(checkIn).format(DAY_MONTH_NAME_SHORT)} - ${moment(
          checkOut,
        ).format(DAY_MONTH_NAME_SHORT)}` :
      undefined
  const guests = occupancies ?
      formatOccupantsAsGuests(...occupancies) :
    undefined

  if (dates && guests) {
    return intl.formatMessage(
      {
        id: 'XBsDoY2xCwFl7ndJzKgT',
        defaultMessage: 'Details updated to {dates}, {guests}',
        description: 'Snackbar message when saved home/villa bookmark dates and guests are updated',
      },
      { dates, guests },
    )
  } else if (dates) {
    return intl.formatMessage(
      {
        id: 'wOhf3TUFcIxCLLYZU3lT',
        defaultMessage: 'Details updated to {dates}',
        description: 'Snackbar message when saved home/villa bookmark dates are updated',
      },
      { dates },
    )
  } else if (guests) {
    return intl.formatMessage(
      {
        id: 'OvZU99ihEgzXYO6CRMVi',
        defaultMessage: 'Details updated to {guests}',
        description: 'Snackbar message when saved home/villa bookmark guests are updated',
      },
      { guests },
    )
  } else {
    return intl.formatMessage({
      id: 'lQfS9mgfI2IOnnGnJ38q',
      defaultMessage: 'Update successful',
      description: 'Generic snackbar message when saved item is updated successfully',
    })
  }
}

export const useSyncHomeAndVillasTripItem = (
  state: OfferPageState,
  offer: App.Offer,
) => {
  const intl = useIntl()
  const awaitingFilters = useRef(false)
  const attemptUpdates = useRef(false)
  const currentTripId = useAppSelector(getImmersiveTripId)
  const { currentCurrency, currentRegionCode } = useContext(GeoContext)
  const { data: currentTrip } = useTrip({ tripId: currentTripId })
  const { showUpdateErrorSnackbar, showUpdateSuccessSnackbar } =
    useUpdateSavedItemBookmarkHandlers()

  const homeAndVillasItemsRef = useRef<Array<AccommodationItem> | null>(null)
  homeAndVillasItemsRef.current = currentTrip?.items.filter(
    (item: TripItem): item is AccommodationItem =>
      item.type === 'ACCOMMODATION' &&
      !item.isBooked &&
      item.code === offer.id,
  ) ?? null

  const { mutate: updateMutate } = useUpdateTripItem({
    onSuccess: (item) => {
      // If the hotelItemRef exists, then we have a bookmarked item
      const savedHomeAndVillasItem = item as Extract<
        LETripItem,
        AccommodationItem
      >
      showUpdateSuccessSnackbar(
        getHomeAndVillasUpdateSuccessSnackbarMessage(
          intl,
          savedHomeAndVillasItem.savedItemData,
        ),
        item.tripId,
        item.id,
      )
    },
    onError: showUpdateErrorSnackbar,
  })

  const datesAndGuests = getDatesAndGuestsFromOfferPageState(state)
  const datesOrGuestsChanged = useDatesOrGuestsChanged(datesAndGuests)
  const filtersApplied = !state.activeFilter
  const accommodationRateRef = useRef<
    ReturnType<typeof useOfferPageRate> | undefined
  >(undefined)
  const accommodationRate = useOfferPageRate(state)
  accommodationRateRef.current = accommodationRate
  const debouncedUpdate = useMemo(
    () =>
      debounce((datesAndGuests: BookmarkDatesAndOccupancy) => {
        const items = homeAndVillasItemsRef.current
        const hotelRate = accommodationRateRef.current
        if (!items || !hotelRate) return

        attemptUpdates.current = false

        items.forEach((item) => {
          const payload = createHomeAndVillasItemPayload({
            offer,
            datesAndGuests,
            currentRegionCode,
            currencyCode: currentCurrency,
            price: hotelRate.hotelPrice,
            value: hotelRate.hotelValue,
            roomTypeId: item.subCode,
          })
          updateMutate({
            tripId: item.tripId,
            tripItemId: item.id,
            tripItem: payload,
          })
        })
      }, 2000),
    [currentCurrency, currentRegionCode, offer, updateMutate],
  )

  useEffect(() => {
    if (datesOrGuestsChanged) {
      awaitingFilters.current = true
    }
    if (awaitingFilters.current && filtersApplied) {
      awaitingFilters.current = false
      attemptUpdates.current = true
    }
    if (attemptUpdates.current) {
      debouncedUpdate(datesAndGuests)
    }
  })
}
